<template>
    <div class="promotion-allocation-area">
        <div v-if="selectedResourceDefinition">
            <div class="promotion-allocation-area__title">
                <label class="font-weight-bold">
                    {{ toSentenceCase($t('preparation.allocation.promotions')) }}
                </label>
            </div>
            <promo-ag-grid
                :row-data="rowData"
                :column-defs="columnDefs"
                :grid-options="gridOptions"
                :default-col-def="defaultColDef"
                :resize-to-fit="false"
                dom-layout="autoHeight"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { filter, map, includes, find, flatMap, get, isNil, keyBy } from 'lodash';
import { markRaw } from 'vue';
import categoryAllocationsTypes from '@enums/category-allocations-types';
import leafletPageTypes from '@enums/leaflet-page-types';
import detailedProvisionsNamesEnum from '@enums/detailed-provisions-names';
import featureFlags from '@enums/feature-flags';
import promotionUtils from '@sharedModules/promotion-utils';
import createFeatureAwareFactory from '@/js/feature-toggles/feature-factory';
import { toSentenceCase } from '@/js/utils/string-utils';
import vuexComponentMixin from '@/js/mixins/vuex-component';

import PromotionRagColourRenderer from './cell-renderers/promotion-rag-colour-renderer.vue';
import PromotionDateRenderer from './cell-renderers/promotion-date-renderer.vue';
import PromotionSelectButtonRenderer from './cell-renderers/promotion-select-button-renderer.vue';
import PromotionExpandButtonRenderer from './cell-renderers/promotion-expand-button-renderer.vue';
import PromotionKpiRenderer from './cell-renderers/promotion-kpi-renderer.vue';
import PromotionProductsRowRenderer from './cell-renderers/promotion-products-row-renderer.vue';

const { isCategoryOrStoreWidePromotion } = promotionUtils;
const { canEditChannel } = featureFlags;
const SELECT_BUTTON_COL_ID = 'selectButton';

export default {
    mixins: [vuexComponentMixin],

    data() {
        return {
            defaultColDef: {
                suppressMovable: true,
                lockPinned: true,
                sortable: false,
                resizable: false,
                filter: false,
                suppressHeaderMenuButton: true,
                menuTabs: [],
                cellClass: 'promotion-allocation-area__grid-cell--promotion-content',
            },
            gridOptions: markRaw({
                headerHeight: 45,
                rowHeight: 42,
                suppressHorizontalScroll: true,
                suppressCellFocus: true,
                isFullWidthRow: ({ rowNode }) => !!rowNode.data?.isExpansion,
                fullWidthCellRenderer: markRaw(PromotionProductsRowRenderer),
                getRowId: ({ data }) =>
                    data.isExpansion ? `expansion-${data.promotionId}` : data._id,
                // Preserve heights set via node.setRowHeight() so resetRowHeights()
                // does not override the expansion row back to the grid default.
                getRowHeight: ({ node }) => {
                    if (node.data?.isExpansion) return node.rowHeight;
                    return undefined;
                },
                onCellClicked: ({ data, colDef }) => {
                    if (data?.isExpansion || colDef?.colId === SELECT_BUTTON_COL_ID) return;
                    this.toggleSelectedPromotion({ promotionId: data._id });
                },
            }),
        };
    },

    computed: {
        ...mapState('clientConfig', ['hierarchyConfig', 'toggleLogic']),
        ...mapState('promotions', ['selectedPromotionId']),
        ...mapState('subCampaigns', ['selectedResourceDefinitionPageNumber', 'selectedAreaData']),
        ...mapGetters('subCampaigns', [
            'selectedSubCampaign',
            'selectedResourceDefinition',
            'selectedResourceDefinitionPage',
            'isPagesResource',
            'isCover',
        ]),
        ...mapGetters('scenarios', ['activeScenariosBySubCampaignId']),
        ...mapGetters('promotions', ['getNominatedPromotions']),
        ...mapGetters('clientConfig', ['getHierarchyConfig']),

        nominatedPromotions() {
            return this.getNominatedPromotions(instance =>
                this.isLocationInstanceHasCurrentPageType(instance)
            );
        },

        featureAwareFactory() {
            return createFeatureAwareFactory(this.toggleLogic);
        },

        filteredPromotions() {
            // Retrieve the active scenarios for the sub-campaign i.e. where 'isFavourite' is true.
            const activeScenariosForSelectedSubCampaign = this.activeScenariosBySubCampaignId(
                this.selectedSubCampaign._id
            );
            // all promotions that appear in the favourite scenario
            const filteredNominatedPromotions = filter(this.nominatedPromotions, promotion =>
                includes(map(activeScenariosForSelectedSubCampaign, '_id'), promotion.scenarioId)
            );
            // Return all promotions that appear in the favourite scenario
            // if no areas have been selected or blank area is selected
            if (!this.selectedAreaData || this.selectedAreaData.categoryKey === null) {
                return filteredNominatedPromotions;
            }

            const promotionAllocationFilterFunction = this.featureAwareFactory.getPromotionAllocationFilterFunction();

            return promotionAllocationFilterFunction({
                scenarios: activeScenariosForSelectedSubCampaign,
                promotions: filteredNominatedPromotions,
                categoryKey: this.selectedAreaData.categoryKey,
                categoryLevel: this.getHierarchyConfig.categoryLevel,
            });
        },

        // ex: {'promotionId': {'promotionId': '1111', 'categoryKey": '1010', products: []}}
        selectedResourceDefinitionAssignmentsMap() {
            if (this.selectedResourceDefinition?.pageCount) {
                const assignmentsArray = flatMap(
                    this.selectedResourceDefinition.pages,
                    page => page.assignment
                );
                return keyBy(assignmentsArray.filter(a => a.promotionId), 'promotionId');
            }
            return {};
        },

        rowData() {
            return this.filteredPromotions.reduce((rows, promotion) => {
                rows.push(promotion);
                if (this.isSelected(promotion)) {
                    rows.push({ isExpansion: true, promotionId: promotion._id });
                }
                return rows;
            }, []);
        },

        columnDefs() {
            return [
                {
                    headerName: '',
                    field: 'effectivenessRating',
                    cellRenderer: markRaw(PromotionRagColourRenderer),
                    maxWidth: 60,
                },
                {
                    headerName: '',
                    cellRenderer: markRaw(PromotionDateRenderer),
                    minWidth: 120,
                },
                {
                    headerName: toSentenceCase(
                        this.$t('preparation.allocation.columnHeaders.promotion')
                    ),
                    field: 'name',
                    cellClass: params => this.getTextCellClasses(params.data),
                    // flex: 1,
                    minWidth: 100,
                },
                {
                    headerName: toSentenceCase(
                        this.$t('preparation.allocation.columnHeaders.promotionMechanic')
                    ),
                    valueGetter: params => get(params.data, 'offerMechanic.description', ''),
                    cellClass: params => this.getTextCellClasses(params.data),
                    flex: 1,
                    minWidth: 120,
                },
                {
                    headerName: toSentenceCase(
                        this.$t('preparation.allocation.columnHeaders.slotSize')
                    ),
                    valueGetter: params => this.getSlotSize(params.data),
                    cellClass: params => this.getTextCellClasses(params.data),
                    maxWidth: 68,
                },
                ...(this.toggleLogic[canEditChannel]
                    ? [
                          {
                              colId: SELECT_BUTTON_COL_ID,
                              headerName: '',
                              cellRenderer: markRaw(PromotionSelectButtonRenderer),
                              cellRendererParams: {
                                  isPromotionDisabled: promotion =>
                                      this.isPromotionDisabled(promotion),
                                  setPromotion: (promotion, event) =>
                                      this.setPromotion(promotion, event),
                              },
                              width: 80,
                          },
                      ]
                    : []),
                {
                    headerName: '',
                    cellRenderer: markRaw(PromotionExpandButtonRenderer),
                    cellRendererParams: {
                        isSelected: promotion => this.isSelected(promotion),
                    },
                    cellClass: [
                        'promotion-allocation-area__grid-cell--promotion-content',
                        'last-column',
                    ],
                    width: 50,
                },
                {
                    headerName: toSentenceCase(
                        this.$t('preparation.allocation.columnHeaders.incrementalSalesEfficiency')
                    ),
                    cellRenderer: markRaw(PromotionKpiRenderer),
                    cellRendererParams: {
                        incrementalAmountPath:
                            'forecastingAggregations.promotion.incrementalSalesExcTax',
                        efficiencyPath: 'forecastingAggregations.promotion.salesEfficiency',
                    },
                    cellClass: [
                        'promotion-allocation-area__grid-cell--promotion-content',
                        'promotion-allocation-area__grid-cell--sidebar',
                    ],
                    width: 110,
                },
                {
                    headerName: toSentenceCase(
                        this.$t('preparation.allocation.columnHeaders.incrementalMarginEfficiency')
                    ),
                    cellRenderer: markRaw(PromotionKpiRenderer),
                    cellRendererParams: {
                        incrementalAmountPath:
                            'forecastingAggregations.promotion.incrementalMargin',
                        efficiencyPath: 'forecastingAggregations.promotion.marginEfficiency',
                    },
                    cellClass: [
                        'promotion-allocation-area__grid-cell--promotion-content',
                        'promotion-allocation-area__grid-cell--sidebar',
                    ],
                    width: 110,
                },
            ];
        },
    },

    methods: {
        ...mapActions('promotions', ['toggleSelectedPromotion', 'setSelectedPromotion']),
        ...mapMutations('subCampaigns', ['setSelectedAreaData']),

        setPromotion({ _id }, event) {
            this.emitter.emit('set-promotion', _id);

            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
        },

        isPromotionDisabled({ _id }) {
            if (this.isReadOnly) return true;

            // as a promotion can only appear once in a leaflet,
            // check if current promotion _id is assigned to any space
            if (this.selectedAreaData && isNil(this.selectedAreaData.promotionId)) {
                return !!this.selectedResourceDefinitionAssignmentsMap[_id];
            }
            return true;
        },

        isSelected(promotion) {
            return (
                promotion._id === this.selectedPromotionId &&
                !isCategoryOrStoreWidePromotion(promotion)
            );
        },

        isLocationInstanceHasCurrentPageType(instance) {
            if (this.isPagesResource) {
                const locationProvision = instance.detailedProvision.find(
                    dp => dp.name === detailedProvisionsNamesEnum.location
                );

                if (locationProvision) {
                    if (locationProvision.value === leafletPageTypes.unspecified) {
                        return true;
                    }
                    return this.isCover
                        ? locationProvision.value === leafletPageTypes.cover
                        : locationProvision.value === leafletPageTypes.inside;
                }
            }
            // leaflet: if locationProvision is not defined, show promotions for any page
            // tv, radio: don't additionally check promotion resources instances
            return true;
        },

        getSlotSize({ resources } = {}) {
            if (!this.selectedResourceDefinition || !resources) return '';
            const { resourceKey, key } = this.selectedResourceDefinition;
            const selectedResource = find(resources, { type: resourceKey });
            if (!selectedResource) return '';
            const filteredLocationInstance = find(
                selectedResource.instances,
                instance =>
                    instance.key === key && this.isLocationInstanceHasCurrentPageType(instance)
            );
            if (!filteredLocationInstance) return '';
            const slotSizeDP = find(filteredLocationInstance.detailedProvision, {
                name: detailedProvisionsNamesEnum.slotSize,
            });
            return slotSizeDP ? toSentenceCase(slotSizeDP.value) : '';
        },

        getTextCellClasses(data) {
            const classes = [
                'promotion-allocation-area__grid-cell--promotion-content',
                'promotion-allocation-area__grid-cell--promotion-content-text',
                'promotion-allocation-area__data',
            ];
            if (data?.effectivenessRating) {
                classes.push(
                    `promotion-allocation-area__border-colour--${data.effectivenessRating}`
                );
            }
            return classes;
        },
    },

    events: {
        updateSelectedArea(area) {
            const assignedArea = find(this.selectedResourceDefinitionPage.assignment, { area });

            if (
                assignedArea &&
                assignedArea.categoryKey !== categoryAllocationsTypes.nonAllocated
            ) {
                if (this.selectedAreaData === assignedArea.area) {
                    this.setSelectedAreaData(null);
                } else {
                    this.setSelectedAreaData({ ...assignedArea });
                }
            } else if (!area) {
                this.setSelectedAreaData(null);
            }
            this.setSelectedPromotion({
                promotionId: this.selectedAreaData ? this.selectedAreaData.promotionId : null,
            });
        },
        changeSelectedPage() {
            this.setSelectedAreaData(null);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

$grid-padding: 0.5rem;

.promotion-allocation-area {
    padding-bottom: $grid-padding;
    font-size: 1.2rem;
    padding-left: 1rem;
    z-index: $promo-details-z-index;

    &__title {
        padding-bottom: 0.5rem;
    }

    :deep(.ag-cell) {
        padding: 0;
        display: block;
    }

    :deep(.ag-header-cell) {
        padding-left: 1rem;
        background-color: $promo-table-blue-bg-colour-3 !important;
        @include promo-component-border($top: true);
        height: 4.5rem;
        font-size: 1.2rem;
    }

    :deep(.ag-row) {
        background-color: transparent !important;
    }

    :deep(.promotion-allocation-area__grid-cell--promotion-content) {
        padding-top: $grid-padding;
    }

    :deep(.promotion-allocation-area__data) {
        @include flex-column;
        padding-top: 0.75rem;
        padding-left: 1rem;
        min-height: 3.5rem;
        background-color: $promo-white;
        border-top-width: 0.2rem;
        border-top-style: solid;
        border-bottom: $border-shadow 0.1rem solid !important;
        font-weight: 600;
        line-height: 1.5rem;
        font-size: 1.2rem;
        color: $text-colour;
        letter-spacing: 0;
        box-shadow: 0 0.4rem 0.4rem 0 $box-shadow-color, 0 0.2rem 0 0 $box-shadow-color;

        &:hover {
            cursor: pointer;
        }
    }

    :deep(.promotion-allocation-area__grid-cell--promotion-content-text) {
        margin-top: $grid-padding;
        height: 3.5rem;
        display: block;
    }

    :deep(.promotion-allocation-area__grid-cell--sidebar) {
        z-index: $promo-details-z-index;
        background-color: $promo-white;

        .promotion-allocation-area__data {
            border-bottom: none;
            box-shadow: none;
        }
    }

    :deep(.promotion-allocation-area__grid-cell--button) {
        display: block;

        .promotion-allocation-area__data {
            padding-top: 0.45rem;
        }
    }

    :deep(.promotion-allocation-area__grid-cell--truncated-content) {
        padding-bottom: 0.2rem;
    }

    :deep(.last-column) {
        padding-right: 0.75rem;

        .promotion-allocation-area__data {
            padding-top: 0;
            justify-content: center;
        }
    }
}
</style>

<style lang="scss">
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.promotion-allocation-area__data {
    border-top-color: $promo-divider-accent-colour !important;
}

.promotion-allocation-area__border-colour {
    @include promo-rag-colours(border-top-color, !important);
}
</style>
